/**
 * Stations and devices (plan §9.5, §10.2, §11): station profiles and printer
 * configuration, pairing keys and their QR payload, station app versions and the
 * minimum-version gate, and the stuck-command thresholds (D-024).
 * @module
 */
import { z } from 'zod';
import { DEVICE_CODE_ALPHABET } from '../numbering/index.js';

// ---------------------------------------------------------------------------------------
// Stations

/**
 * The view a station wakes up on (plan §9.1). A profile is configuration, never a gate:
 * every station can open every view its staff may use.
 */
export const STATION_PROFILES = [
  'production',
  'receiving',
  'stock_take',
  'expiry',
  'labels',
] as const;
/** One of {@link STATION_PROFILES}. */
export type StationProfile = (typeof STATION_PROFILES)[number];
/** zod schema for a station profile. */
export const stationProfileSchema = z.enum(STATION_PROFILES);

/** Platforms a station app runs on (D-017). */
export const DEVICE_PLATFORMS = ['android', 'ios', 'windows', 'web'] as const;
/** One of {@link DEVICE_PLATFORMS}. */
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];
/** zod schema for a device platform. */
export const devicePlatformSchema = z.enum(DEVICE_PLATFORMS);

/** The raw-TCP port network label printers listen on. */
export const DEFAULT_PRINTER_PORT = 9100;

const PRINTER_HOST_RE = /^[A-Za-z0-9](?:[A-Za-z0-9.-]{0,251}[A-Za-z0-9])?$/;

/** A station's network label printer (ZPL over raw TCP, plan §9.6). Not a secret. */
export const stationPrinterSchema = z.object({
  /** IP address or host name on the shop's network, e.g. `192.168.1.50`. */
  host: z
    .string()
    .trim()
    .regex(PRINTER_HOST_RE, 'Use an address like 192.168.1.50 or a printer name'),
  port: z.int().min(1).max(65535),
  /** The label stock loaded (same list as `@enp/domain/labels` `LabelSize`). */
  label_size: z.enum(['58x40mm', '50x25mm']),
});
/** A station's printer. */
export type StationPrinter = z.infer<typeof stationPrinterSchema>;

/**
 * `station.hardware_config`: what a station owns. Unknown keys are kept (a later station
 * may own a scale or a drawer), so an older server never drops them.
 */
export const stationHardwareSchema = z.looseObject({
  printer: stationPrinterSchema.nullable().optional(),
});
/** A station's hardware configuration. */
export type StationHardware = z.infer<typeof stationHardwareSchema>;

/** The printer in a stored `hardware_config`, or null when none (or it is unreadable). */
export function printerOf(hardwareConfig: unknown): StationPrinter | null {
  const parsed = stationHardwareSchema.safeParse(hardwareConfig);
  return parsed.success ? (parsed.data.printer ?? null) : null;
}

// ---------------------------------------------------------------------------------------
// Pairing (plan §10.2): a branch code + a one-time access key, valid 10 minutes

/** How long an access key can be used after it was issued. */
export const PAIRING_TTL_MS = 10 * 60_000;

/** Characters in an access key: the device-code alphabet (no O/0, I/1/L). */
export const ACCESS_KEY_ALPHABET = DEVICE_CODE_ALPHABET;
/** Characters in an access key (31¹² ≈ 2⁵⁹ keys). */
export const ACCESS_KEY_LENGTH = 12;

const ACCESS_KEY_RE = new RegExp(`^[${ACCESS_KEY_ALPHABET}]{${ACCESS_KEY_LENGTH}}$`);
// Largest multiple of the alphabet size below 256: bytes above it are skipped (no bias).
const BYTE_LIMIT = 256 - (256 % ACCESS_KEY_ALPHABET.length);

/**
 * Builds an access key from random bytes (the server passes CSPRNG output). Bytes that
 * would bias the alphabet are skipped, so give a few spare (24 bytes is always plenty in
 * practice). Throws when the bytes run out first. Returns the plain 12 characters; show it
 * with {@link formatAccessKey}.
 */
export function accessKeyFromBytes(bytes: Uint8Array): string {
  let key = '';
  for (const b of bytes) {
    if (b >= BYTE_LIMIT) continue;
    key += ACCESS_KEY_ALPHABET[b % ACCESS_KEY_ALPHABET.length];
    if (key.length === ACCESS_KEY_LENGTH) return key;
  }
  throw new RangeError('Not enough random bytes for an access key');
}

/** `ABCDEFGHJKMN` → `ABCD-EFGH-JKMN`: groups of four, easy to read out and type. */
export function formatAccessKey(key: string): string {
  const plain = normaliseAccessKey(key);
  if (!plain) throw new RangeError('Not an access key');
  return `${plain.slice(0, 4)}-${plain.slice(4, 8)}-${plain.slice(8)}`;
}

/**
 * What someone typed or scanned → the plain 12-character key, or null. Case, spaces and
 * hyphens don't matter; any other character (including O/0, I/1/L) makes it invalid.
 */
export function normaliseAccessKey(input: string): string | null {
  const plain = input.replace(/[\s-]/g, '').toUpperCase();
  return ACCESS_KEY_RE.test(plain) ? plain : null;
}

/** Prefix of the pairing QR payload. Brand-neutral, like the lot QR (D-011). */
export const PAIRING_QR_PREFIX = 'PAIR:';

/**
 * The QR shown in the Admin when a device is paired: `PAIR:<branch code>:<access key>`,
 * e.g. `PAIR:MAIN:ABCD-EFGH-JKMN`. A station scans it instead of typing both.
 */
export function encodePairingQr(branchCode: string, accessKey: string): string {
  const code = branchCode.trim();
  if (code === '') throw new RangeError('branchCode must not be blank');
  return `${PAIRING_QR_PREFIX}${code}:${formatAccessKey(accessKey)}`;
}

/** Reads a pairing QR back (case-insensitive prefix), or null when it isn't one. */
export function parsePairingQr(text: string): { branchCode: string; accessKey: string } | null {
  const value = text.trim();
  if (value.slice(0, PAIRING_QR_PREFIX.length).toUpperCase() !== PAIRING_QR_PREFIX) return null;
  const rest = value.slice(PAIRING_QR_PREFIX.length);
  // The key has no ':'; a branch code may.
  const at = rest.lastIndexOf(':');
  if (at <= 0) return null;
  const branchCode = rest.slice(0, at).trim();
  const accessKey = normaliseAccessKey(rest.slice(at + 1));
  return branchCode && accessKey ? { branchCode, accessKey } : null;
}

// ---------------------------------------------------------------------------------------
// App versions and the minimum-version gate (plan §9.5, §11 "clients reload on release")

/**
 * The station app's current release. The minimum version a new account starts with, per
 * platform (an Admin setting). Raise the setting when a release must reach every station.
 */
export const CURRENT_STATION_APP_VERSION = '0.1.0';

/** `MAJOR.MINOR.PATCH`, optionally `-prerelease` and `+build` (semver shape). */
const APP_VERSION_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

/** A parsed app version. */
export interface AppVersion {
  major: number;
  minor: number;
  patch: number;
  /** Pre-release identifiers (`1.2.0-beta.2` → `['beta', '2']`); empty for a release. */
  prerelease: string[];
}

/** Parses `1.2.3`, `1.2.3-beta.1`, `1.2.3+42`; null for anything else. */
export function parseAppVersion(text: string): AppVersion | null {
  const m = APP_VERSION_RE.exec(text.trim());
  if (!m) return null;
  const nums = [m[1], m[2], m[3]].map(Number);
  if (!nums.every(Number.isSafeInteger)) return null;
  return {
    major: nums[0] as number,
    minor: nums[1] as number,
    patch: nums[2] as number,
    prerelease: m[4] ? m[4].split('.') : [],
  };
}

/** True for a version {@link parseAppVersion} accepts. */
export function isAppVersion(text: string): boolean {
  return parseAppVersion(text) !== null;
}

/** zod schema for an app version string. */
export const appVersionSchema = z
  .string()
  .max(64)
  .refine(isAppVersion, { message: 'Use a version like 1.4.2' });

function comparePrerelease(a: string[], b: string[]): number {
  // A release sorts after its pre-releases.
  if (a.length === 0 || b.length === 0) return b.length - a.length;
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const x = a[i] as string;
    const y = b[i] as string;
    if (x === y) continue;
    const xn = /^\d+$/.test(x);
    const yn = /^\d+$/.test(y);
    if (xn && yn) return Number(x) - Number(y);
    if (xn !== yn) return xn ? -1 : 1;
    return x < y ? -1 : 1;
  }
  return a.length - b.length;
}

/**
 * Semver order: −1, 0 or 1. Build metadata is ignored; `1.2.0-beta < 1.2.0`. Throws
 * `RangeError` on a version {@link parseAppVersion} refuses.
 */
export function compareAppVersions(a: string, b: string): -1 | 0 | 1 {
  const x = parseAppVersion(a);
  const y = parseAppVersion(b);
  if (!x || !y) throw new RangeError(`Not an app version: ${JSON.stringify(x ? b : a)}`);
  const d =
    x.major - y.major ||
    x.minor - y.minor ||
    x.patch - y.patch ||
    comparePrerelease(x.prerelease, y.prerelease);
  return d < 0 ? -1 : d > 0 ? 1 : 0;
}

/**
 * True when a station at `version` must update before it may sync (`upgrade_required`).
 * A version that can't be read is treated as too old: the gate fails closed.
 */
export function isBelowMinVersion(version: string, minimum: string): boolean {
  if (!isAppVersion(version)) return true;
  return compareAppVersions(version, minimum) < 0;
}

/** The account setting holding a platform's minimum app version. */
export function minAppVersionSettingKey(platform: DevicePlatform): string {
  return `stations.min_app_version.${platform}`;
}

/** Minimum app version per platform. */
export type MinAppVersions = Record<DevicePlatform, string>;

/**
 * Account setting: minutes without a touch before a station signs its person out
 * (D-046; 0 = never). Synced to stations (a `stations.*` key).
 */
export const STATION_AUTO_SIGN_OUT_SETTING_KEY = 'stations.auto_sign_out_minutes';
/** Default auto sign-out (D-046): 10 minutes. */
export const DEFAULT_STATION_AUTO_SIGN_OUT_MINUTES = 10;
/** The longest auto sign-out the Admin allows (a whole shift is 8 hours). */
export const MAX_STATION_AUTO_SIGN_OUT_MINUTES = 480;

/**
 * The auto sign-out minutes from the stored setting: a whole number from 0 to
 * {@link MAX_STATION_AUTO_SIGN_OUT_MINUTES}, else the default (a hand-edited value never
 * switches it off by accident).
 */
export function autoSignOutMinutesFromSetting(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAX_STATION_AUTO_SIGN_OUT_MINUTES
    ? value
    : DEFAULT_STATION_AUTO_SIGN_OUT_MINUTES;
}

/** The defaults: every platform at {@link CURRENT_STATION_APP_VERSION}. */
export const DEFAULT_MIN_APP_VERSIONS: Readonly<MinAppVersions> = Object.freeze(
  Object.fromEntries(
    DEVICE_PLATFORMS.map((p) => [p, CURRENT_STATION_APP_VERSION]),
  ) as MinAppVersions,
);

// ---------------------------------------------------------------------------------------
// Stuck commands (D-024)

/**
 * A command is "stuck" once it was answered `retry_later` more than this many times…
 * (D-024 default 20).
 */
export const STUCK_COMMAND_MAX_RETRIES = 20;
/** …or has been retried for longer than this (D-024 default 6 hours). */
export const STUCK_COMMAND_MAX_AGE_MS = 6 * 3_600_000;

/** Whether a retried command is stuck: too many answers, or retried for too long. */
export function isStuckCommand(
  retry: { count: number; firstAt: Date; lastAt: Date },
  limits: { maxRetries?: number; maxAgeMs?: number } = {},
): boolean {
  const maxRetries = limits.maxRetries ?? STUCK_COMMAND_MAX_RETRIES;
  const maxAgeMs = limits.maxAgeMs ?? STUCK_COMMAND_MAX_AGE_MS;
  return retry.count > maxRetries || retry.lastAt.getTime() - retry.firstAt.getTime() > maxAgeMs;
}
